import React, { useCallback } from 'react';
import { ButtonStyled, useBreakpoint, useMatchMedia } from '@nike/nike-design-system-components';
import { RELIC_ACTION_TYPES_NAME } from '@nike/privacy-shared-logic';

import { usePrivacyConsentContext } from '../../../PrivacyConsentContextProvider';
import { CookieDialogActionsStyled } from './CookieDialogActionsStyled';
import {
  analyticsTrackClickAcceptAll,
  analyticsTrackClickDeclineAll,
} from '../../../../helpers/analyticsHandlers';

/**
 * @name CookieDialogActions
 * @description Component which renders a cookie dialog actions
 * @returns {JSX.Element|null}
 * @memberof mfe-privacy-consent
 */
export const CookieDialogActions = (): JSX.Element | null => {
  const { footer, isLoading, isLearnMoreShown, setRelicActionType, purposeLimitationsCategories } =
    usePrivacyConsentContext();
  const isMobile = useMatchMedia(useBreakpoint('lt', 's'));

  const handleClickDecline = useCallback(() => {
    footer.declineAllButton && footer.declineAllButton.onClick();
    analyticsTrackClickDeclineAll(purposeLimitationsCategories);
    setRelicActionType(RELIC_ACTION_TYPES_NAME.DECLINE_ALL_CLICKED);
  }, [footer.declineAllButton, setRelicActionType, purposeLimitationsCategories]);

  const handleClickAccept = useCallback(() => {
    footer.acceptAllButton && footer.acceptAllButton.onClick();
    analyticsTrackClickAcceptAll(purposeLimitationsCategories);
    setRelicActionType(RELIC_ACTION_TYPES_NAME.ACCEPT_ALL_CLICKED);
  }, [footer.acceptAllButton, setRelicActionType, purposeLimitationsCategories]);

  if (isLearnMoreShown) return null;

  // Empty <div/> with tabIndex - needed to avoid FocusTrap error if we do not have any clickable element in the modal
  if ((!footer.declineAllButton && !footer.acceptAllButton) || isLoading)
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
    return <div tabIndex={0} />;

  return (
    <CookieDialogActionsStyled>
      {footer.acceptAllButton && (
        <ButtonStyled
          appearance="primary"
          className="dialog-actions-accept-btn"
          type="button"
          size={isMobile ? 'large' : 'medium'}
          data-testid="dialog-accept-button"
          ariaLabel={footer.acceptAllButton.title}
          onClick={handleClickAccept}
        >
          {footer.acceptAllButton.title}
        </ButtonStyled>
      )}
      {footer.declineAllButton && (
        <ButtonStyled
          appearance="primary"
          className="dialog-actions-decline-btn"
          type="button"
          size={isMobile ? 'large' : 'medium'}
          data-testid="dialog-decline-button"
          ariaLabel={footer.declineAllButton.title}
          onClick={handleClickDecline}
        >
          {footer.declineAllButton.title}
        </ButtonStyled>
      )}
    </CookieDialogActionsStyled>
  );
};
